import classNames from "classnames";
import React, {
    ReactElement, useEffect, useMemo, useState,
} from "react";
import {
    IntegrationActionType,
    useGetWorkflowServiceLazyQuery, useGetWorkflowServicesQuery,
    WorkflowService,
} from "../../../__generated__/api";
import { ARTICLES } from "../../../components/help/Help";
import Button from "../../../components/theme/button/Button";
import TimedMessage from "../../../components/timed-message/TimedMessage";
import useArticleContext from "../../../contexts/HelpContext";
import useRouting, { buildUrl, Paths } from "../../../contexts/Routing";
import { WorkflowServiceInstall } from "../../administrator/sections/workflow-services/ViewService";
import Styles from "../Onboarding.module.scss";
import { ChoiceButton, StepProps } from "./base";

const OPEN_AI_ID = "urn:fq:workflow-service:c0969c6a0a064ca4a456097d8c5b7da9";

function createUrl(selectedId: string) {
    const url = new URL(
        buildUrl(
            Paths.ONBOARDING_STEP,
            { stepId: "install-ai" },
        ),
        window.location.href,
    );
    url.searchParams.append("serviceId", selectedId);
    return url.toString();
}

function InstallButton({
    onSelected, service, isSelected,
}: {
    onSelected: (service: WorkflowService) => void;
    service: WorkflowService;
    isSelected: boolean;
}): ReactElement {
    return (
        <Button
            variant="outline-gray"
            onClick={() => onSelected(service)}
            metricPath={["onboarding", "enable-ai", "service-selected", service.id]}
            className={classNames(
                Styles["ticketing-option"],
                isSelected && Styles["ticketing-option-selected"],
            )}
        >
            <div className={Styles["ticketing-option-header"]}>
                <img
                    src={service.iconUrl as string}
                    alt={`Install AI Provider ${service.name}`}
                />
            </div>
            <div className={Styles["ticketing-option-connect"]}>
                <div className="d-flex align-items-center gap-2">
                    <h4 className="m-0">{service.name}</h4>
                </div>
                <p>{service.shortDescription}</p>

            </div>
        </Button>
    );
}

export default function OnboardingStep(props: StepProps): ReactElement {
    const [selectedService, setSelectedService] = useState<WorkflowService>();
    const [getService] = useGetWorkflowServiceLazyQuery();
    const { data: installData } = useGetWorkflowServicesQuery({
        variables: {
            includeActions: false,
            includeConnections: true,
            actionTypes: [IntegrationActionType.LlmProvider],
        },
    });

    const { getSearchParams, historyPushSearch } = useRouting();

    const searchParams = getSearchParams(Paths.ONBOARDING_STEP);
    const { error: installError, isSuccess: installSuccess, serviceId: installedServiceId } = searchParams;

    const installService = async (id: string) => {
        if (props.profile.installedWorkflowServiceId === id) {
            props.onNext(props.profile);
            return;
        }
        const service = await getService({
            variables: {
                id,
            },
        });
        if (service.data?.workflowService) {
            setSelectedService(service.data?.workflowService as WorkflowService);
        }
    };

    useEffect(() => {
        if (installSuccess) {
            props.onDone({
                ...props.profile,
                artificialIntelligenceWorkflowServiceId: installedServiceId,
            });
        }
    }, []);

    const defaultServiceId = useMemo(() => installData?.workflowServices.find(service => service.id === OPEN_AI_ID)?.id, [installData]);

    useEffect(() => {
        if (props.stepId === "install-ai") {
            const installed = installData?.workflowServices.find(
                service => service.id === props.profile.artificialIntelligenceWorkflowServiceId,
            );
            if (props.profile.artificialIntelligenceWorkflowServiceId != null && installed?.installedIntegrationIds.isEmpty) {
                installService(props.profile.artificialIntelligenceWorkflowServiceId);
            } else {
                props.goNext();
            }
        }
    }, [props.stepId]);

    useEffect(() => {
        if (defaultServiceId && props.profile.artificialIntelligenceWorkflowServiceId == null) {
            props.onNext({
                ...props.profile,
                artificialIntelligenceWorkflowServiceId: defaultServiceId,
            });
        }
    }, [defaultServiceId]);

    useArticleContext(ARTICLES.ONBOARDING_ENABLE_AI);

    return (
        <div>

            {selectedService && (
                <WorkflowServiceInstall
                    service={selectedService}
                    isInstalling={selectedService != null}
                    onDone={() => {
                        props.onNext({
                            ...props.profile,
                            artificialIntelligenceWorkflowServiceId: selectedService.id,
                        });
                        props.goNext();
                        setSelectedService(undefined);
                    }}
                    onCancel={() => {
                        setSelectedService(undefined);
                        props.goBack();
                    }}
                    onCompletedUrl={createUrl(selectedService.id)}
                    metricPathPrefix={["onboarding", props.stepId, "enable-ai", "install"]}
                />
            )}

            <TimedMessage
                shouldShow={installError != null && installError !== ""}
                title="Error while installing"
                variant="danger"
                onClose={() => {
                    delete (searchParams as Partial<typeof searchParams>).error;
                    historyPushSearch(searchParams);
                }}
            >
                {installError}
            </TimedMessage>

            <div className={Styles["step-header"]}>
                <h2 className={props.isV3 ? Styles["step-title"] : ""}>
                    AI Assistant
                </h2>
                <p>
                    AI supercharges Foqal — auto-routes requests, answers
                    {" "}
                    {props.profile.isExternalSupport ? "customers" : "employees"}
                    {" "}
                    questions, chats with tickets via Ask AI, and even writes articles for you.
                </p>
            </div>

            <div className={Styles["choice-options"]}>

                <ChoiceButton
                    isSelected={props.profile.artificialIntelligenceWorkflowServiceId != null}
                    arrowDirection="down"
                    metricPath={["onboarding", props.stepId, "enable-ai"]}
                    onSelected={() => {
                        props.onNext({
                            ...props.profile,
                            artificialIntelligenceWorkflowServiceId: defaultServiceId,
                        });
                    }}
                >
                    Enable AI
                </ChoiceButton>
                <ChoiceButton
                    isSelected={props.profile.artificialIntelligenceWorkflowServiceId == null}
                    metricPath={["onboarding", props.stepId, "disable-ai"]}
                    onSelected={() => {
                        props.onNext({
                            ...props.profile,
                            artificialIntelligenceWorkflowServiceId: undefined,
                        });
                    }}
                >
                    Disable AI
                </ChoiceButton>

            </div>
            {props.profile.artificialIntelligenceWorkflowServiceId && (
                <div className={Styles["ticketing-options"]}>
                    {installData?.workflowServices.map((service, index) => (
                        <InstallButton
                            onSelected={() => {
                                props.onNext({
                                    ...props.profile,
                                    artificialIntelligenceWorkflowServiceId: service.id,
                                });
                            }}
                            service={service as WorkflowService}
                            isSelected={props.profile.artificialIntelligenceWorkflowServiceId
                                ? props.profile.artificialIntelligenceWorkflowServiceId === service.id
                                : index === 0}
                        />
                    ))}

                </div>
            )}

        </div>
    );
}
